/**
 * Demande de publication sur skills4mation.com (31/10/2026).
 *  - `CartePublication` : sur la fiche d'une formation dont le parcours est enregistré, le formateur prépare sa demande
 *    (visuel de la fiche, commentaire), l'envoie, puis en suit le statut ;
 *  - `PublicationsATraiter` : sur l'accueil de l'organisme, la file des demandes, la décision, et le téléchargement de
 *    la fiche JSON (format exact de la vitrine) et du paquet avec les visuels.
 * La publication effective reste manuelle (commit dans `vitrine/`), sous 48 h après validation : l'écran le dit.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, Eye, FileJson, Globe, Handshake, ImagePlus, Images, Library, Send, Sparkles, Upload, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { api, instantFr, type DemandePublication, type DemandeATraiter, type EtatPublication, type FilePublications, type StatistiquesVues } from "../api";
import { Avatar } from "../ui/Avatar";
import { ResumeVues } from "./MaVitrine";
import { Alerte, Bouton, Carte, Champ, Chargement, cx, Etiquette, Modale, Selecteur, ZoneTexte, useNotifier } from "../ui/base";
import { CATEGORIES_VITRINE, ESSAIS_IA_MAX } from "@/domaine/publication/fiche-vitrine";
import BIBLIOTHEQUE from "@/domaine/publication/bibliotheque-vitrine.json";

const LIBELLES_STATUT = { brouillon: "En préparation", demandee: "Publication demandée", acceptee: "Publication acceptée", refusee: "Publication refusée" } as const;
const TONS_STATUT = { brouillon: "neutre", demandee: "attente", acceptee: "accent", refusee: "danger" } as const;
const SOURCES = { aucun: "Aucun visuel", ia: "Généré par l'IA", televerse: "Image téléversée", bibliotheque: "Visuel de la vitrine" } as const;

type Images = EtatPublication["images"];
type Onglet = "ia" | "bibliotheque" | "televerser";

/** Choix du visuel de la fiche : IA (Gemini), bibliothèque des visuels de la vitrine, ou image téléversée. */
export function PanneauVisuel({ demande, images, alt, setAlt, maj }: { demande: DemandePublication; images: Images; alt: string; setAlt: (x: string) => void; maj: (d: DemandePublication) => void }) {
  const notifier = useNotifier();
  const [onglet, setOnglet] = useState<Onglet>(images.disponible ? "ia" : "bibliotheque");
  const fichier = useRef<HTMLInputElement>(null);
  const apres = (d: DemandePublication) => {
    maj(d);
    setAlt(d.visuel_alt);
  };
  const ia = useMutation({ mutationFn: () => api.post<DemandePublication>(`/publications/${demande.id}/visuel/ia`), onSuccess: apres, onError: (e) => notifier("danger", e.message) });
  const biblio = useMutation({ mutationFn: (image: string) => api.post<DemandePublication>(`/publications/${demande.id}/visuel/bibliotheque`, { image }), onSuccess: apres, onError: (e) => notifier("danger", e.message) });
  const televerser = useMutation({ mutationFn: (f: File) => api.fichier<DemandePublication>(`/publications/${demande.id}/visuel/televerser`, f), onSuccess: apres, onError: (e) => notifier("danger", e.message) });
  const ONGLETS: Array<[Onglet, string, React.ReactNode]> = [
    ["ia", "Générer par l'IA", <Sparkles key="i" className="size-4" aria-hidden />],
    ["bibliotheque", "Bibliothèque de la vitrine", <Library key="b" className="size-4" aria-hidden />],
    ["televerser", "Téléverser une image", <Upload key="t" className="size-4" aria-hidden />],
  ];
  return (
    <section aria-labelledby="titre-visuel" className="space-y-4">
      <h3 id="titre-visuel" className="flex items-center gap-2 text-[15px] font-semibold">
        <Images className="size-4 text-accent" aria-hidden /> Visuel de la fiche
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[240px_minmax(0,1fr)]">
        <figure className="overflow-hidden rounded-md border border-trait bg-papier-2">
          {demande.visuel_url ? (
            <img src={demande.visuel_url} alt={alt || "Aperçu du visuel"} width={400} height={267} className="aspect-[3/2] w-full object-cover" />
          ) : (
            <div className="grid aspect-[3/2] place-items-center px-4 text-center text-[13px] text-encre-3">
              <span>
                <ImagePlus className="mx-auto mb-1 size-6" aria-hidden />
                Aucun visuel choisi
              </span>
            </div>
          )}
          <figcaption className="border-t border-trait px-3 py-1.5 text-xs text-encre-2">{SOURCES[demande.visuel_source]} · format 3:2, 1200/800/400 px</figcaption>
        </figure>
        <div className="min-w-0">
          <div role="tablist" aria-label="Origine du visuel" className="flex flex-wrap gap-1.5">
            {ONGLETS.map(([cle, libelle, icone]) => (
              <button key={cle} type="button" role="tab" aria-selected={onglet === cle} onClick={() => setOnglet(cle)} className={cx("inline-flex h-8 items-center gap-1.5 rounded-sm border px-2.5 text-[13px] font-medium", onglet === cle ? "border-accent bg-accent-doux text-accent-fort" : "border-trait-fort bg-carte text-encre-2 hover:bg-papier-2")}>
                {icone}
                {libelle}
              </button>
            ))}
          </div>
          <div role="tabpanel" className="mt-3">
            {onglet === "ia" &&
              (images.disponible ? (
                <div className="space-y-2">
                  <p className="text-[13px] text-encre-2">
                    Photo réaliste générée d'après le titre, le domaine et le public de la formation : lumière naturelle, sans texte, sans logo, sans visage reconnaissable. {images.description}{/démonstration/.test(images.description) ? "" : `, ${images.cout}`}.
                  </p>
                  <Bouton variante="primaire" taille="sm" icone={<Sparkles className="size-3.5" aria-hidden />} enCours={ia.isPending} disabled={demande.essais_restants === 0} onClick={() => ia.mutate()}>
                    {demande.visuel_source === "ia" ? "Régénérer" : "Générer le visuel"}
                  </Bouton>
                  <p className="text-xs text-encre-3" aria-live="polite">
                    {ia.isPending ? "Génération en cours (10 à 30 s)…" : `Essais restants pour cette demande : ${demande.essais_restants}/${ESSAIS_IA_MAX}.`}
                  </p>
                </div>
              ) : (
                <Alerte ton="attention" titre="Génération d'images non configurée">
                  L'organisme doit renseigner la « Clé d'API Google Gemini (images) » dans Organisme → Assistant IA. En attendant, choisissez un visuel de la bibliothèque ou téléversez votre image.
                </Alerte>
              ))}
            {onglet === "bibliotheque" && (
              <div>
                <p className="mb-2 text-[13px] text-encre-2">Visuels déjà publiés sur skills4mation.com : rien à copier, la fiche y renverra.</p>
                <ul className="grid max-h-64 grid-cols-3 gap-2 overflow-y-auto pr-1 sm:grid-cols-4" aria-label="Bibliothèque de la vitrine">
                  {BIBLIOTHEQUE.map((b) => {
                    const choisi = demande.visuel_vitrine === b.image;
                    return (
                      <li key={b.image}>
                        <button type="button" aria-pressed={choisi} title={b.fiche} disabled={biblio.isPending} onClick={() => biblio.mutate(b.image)} className={cx("relative block w-full overflow-hidden rounded-sm border-2", choisi ? "border-accent" : "border-transparent hover:border-trait-fort")}>
                          <img src={b.vignette} alt={b.alt} loading="lazy" width={400} height={267} className="aspect-[3/2] w-full object-cover" />
                          {choisi && <Check className="absolute top-1 right-1 size-5 rounded-full bg-accent p-0.5 text-sur-accent" aria-hidden />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {onglet === "televerser" && (
              <div className="space-y-2">
                <p className="text-[13px] text-encre-2">JPG, PNG ou WebP, 5 Mo au plus. L'image est recadrée en 3:2 et déclinée en WebP 1200, 800 et 400 px. N'utilisez que des images dont vous détenez les droits.</p>
                <input ref={fichier} type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="sr-only" aria-label="Choisir une image" onChange={(e) => { const f = e.target.files?.[0]; if (f) televerser.mutate(f); e.target.value = ""; }} />
                <Bouton taille="sm" icone={<Upload className="size-3.5" aria-hidden />} enCours={televerser.isPending} onClick={() => fichier.current?.click()}>Choisir une image</Bouton>
              </div>
            )}
          </div>
        </div>
      </div>
      {demande.visuel_source !== "aucun" && <Champ libelle="Texte alternatif du visuel" aide="Décrit l'image pour les lecteurs d'écran et le référencement (10 à 200 caractères)." value={alt} maxLength={200} onChange={(e) => setAlt(e.target.value)} />}
    </section>
  );
}

/** Case à cocher simple, au style de la charte (cible ≥ 24 px). */
function Case({ coche, changer, children, id }: { coche: boolean; changer: (v: boolean) => void; children: React.ReactNode; id: string }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-2.5 text-sm leading-snug text-encre">
      <input id={id} type="checkbox" checked={coche} onChange={(e) => changer(e.target.checked)} className="mt-0.5 size-4.5 shrink-0 accent-(--color-accent)" />
      <span>{children}</span>
    </label>
  );
}

export interface ChoixAmbassadeur {
  ambassadeur: boolean;
  afficher: boolean;
  image: boolean;
}

/**
 * Programme ambassadeur (04/11/2026) : engagement de relais des demandes (adhésion, texte versionné) et option « nom et
 * photo sur la fiche » avec consentement explicite (droit à l'image). Aperçu du bloc « Votre formateur ».
 */
export function PanneauAmbassadeur({ e, choix, changer }: { e: EtatPublication["ambassadeur"]; choix: ChoixAmbassadeur; changer: (c: ChoixAmbassadeur) => void }) {
  return (
    <section aria-labelledby="titre-ambassadeur" className="space-y-4 rounded-md border border-accent/40 bg-accent-doux p-4" data-panneau="ambassadeur">
      <div>
        <h3 id="titre-ambassadeur" className="flex items-center gap-2 text-[15px] font-semibold text-encre">
          <Handshake className="size-4.5 text-accent" aria-hidden /> Programme ambassadeur
        </h3>
        <p className="mt-1 text-sm font-medium text-encre">Chaque demande reçue via cette page vous est transmise.</p>
        <p className="mt-0.5 text-[13px] text-encre-2">La fiche affiche alors un formulaire « Une question sur cette formation ? » ; Skills4mation vous relaie chaque demande par e-mail et dans « Ma page sur le site ».</p>
      </div>
      <Case id="case-ambassadeur" coche={choix.ambassadeur} changer={(v) => changer({ ...choix, ambassadeur: v })}>
        {e.engagement.texte} <span className="text-xs text-encre-3">(version {e.engagement.version})</span>
      </Case>
      <div className="border-t border-accent/25 pt-4">
        <Case id="case-afficher" coche={choix.afficher} changer={(v) => changer({ ...choix, afficher: v, image: v ? choix.image : false })}>
          <span className="font-medium">Afficher mon nom et ma photo de profil sur la fiche</span> (bloc « Votre formateur »)
        </Case>
        {choix.afficher && (
          <div className="mt-3 space-y-3 pl-7">
            <div className="flex items-center gap-3 rounded-md border border-trait bg-carte p-3" aria-label="Aperçu du bloc sur la fiche">
              <Avatar nom={e.apercu.nom} photo={e.apercu.photo} taille="lg" />
              <div className="min-w-0">
                <p className="text-xs text-encre-3">Votre formateur</p>
                <p className="truncate font-semibold">{e.apercu.nom}</p>
              </div>
            </div>
            {!e.apercu.photo && (
              <p className="text-xs text-encre-2">
                Pas encore de photo de profil : vos initiales s'afficheront. <Link to="/profil" className="text-accent underline">Ajouter une photo</Link>
              </p>
            )}
            <Case id="case-image" coche={choix.image} changer={(v) => changer({ ...choix, image: v })}>
              {e.image.texte} <span className="text-xs text-encre-3">(version {e.image.version})</span>
            </Case>
          </div>
        )}
      </div>
      <p className="text-xs text-encre-3">Votre accord est horodaté et conservé avec la demande. Mention affichée sous le formulaire de la fiche : « {e.mention_formulaire} »</p>
    </section>
  );
}

/** Vues de la fiche publiée (chiffres arrêtés à la veille), sur la carte de la formation. */
function VuesDeLaFiche({ slug }: { slug: string }) {
  const vues = useQuery({ queryKey: ["vues-fiches"], queryFn: () => api.get<StatistiquesVues>("/formateur/vues") });
  const f = vues.data?.formations.find((x) => x.slug === slug);
  if (!f) return null;
  return (
    <div className="mt-4 space-y-2">
      <p className="flex items-center gap-1.5 text-[13px] font-medium text-encre-2">
        <Eye className="size-4 text-accent" aria-hidden /> Vues de la page
      </p>
      <ResumeVues f={f} />
      <Link to="/ma-vitrine" className="inline-block text-[13px] font-medium text-accent hover:underline">
        Historique et demandes reçues →
      </Link>
    </div>
  );
}

/** Statut de la dernière demande, tel que le formateur le voit sur sa formation. */
function StatutDemande({ d, delai }: { d: DemandePublication; delai: string }) {
  return (
    <div className="mt-3 space-y-1.5 text-sm" role="status">
      <Etiquette ton={TONS_STATUT[d.statut]}>{LIBELLES_STATUT[d.statut]}</Etiquette>
      {d.statut === "demandee" && <p className="text-encre-2">Demandée le {instantFr(d.demandee_le)}. L'organisme l'examine ; vous serez prévenu par e-mail.</p>}
      {d.statut === "acceptee" && <p className="text-encre-2">Acceptée le {instantFr(d.traitee_le)} — {delai}.</p>}
      {d.statut === "refusee" && (
        <p className="text-encre-2">
          Refusée le {instantFr(d.traitee_le)}. Motif : <span className="text-encre">{d.motif_refus}</span>
        </p>
      )}
    </div>
  );
}

export function CartePublication({ formationId }: { formationId: string }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const etat = useQuery({ queryKey: ["publication", formationId], queryFn: () => api.get<EtatPublication>(`/formations/${formationId}/publication`) });
  const [ouverte, setOuverte] = useState(false);
  const [demande, setDemande] = useState<DemandePublication | null>(null);
  const [alt, setAlt] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [choix, setChoix] = useState<ChoixAmbassadeur>({ ambassadeur: false, afficher: false, image: false });
  const preparer = useMutation({
    mutationFn: () => api.post<DemandePublication>(`/formations/${formationId}/publication`),
    onSuccess: (d) => { setDemande(d); setAlt(d.visuel_alt); setOuverte(true); },
    onError: (e) => notifier("danger", e.message),
  });
  const soumettre = useMutation({
    mutationFn: () => api.post<DemandePublication>(`/publications/${demande!.id}/soumettre`, {
        commentaire,
        ...(demande!.visuel_source !== "aucun" ? { visuel_alt: alt } : {}),
        ambassadeur: choix.ambassadeur,
        ambassadeur_version: etat.data!.ambassadeur.engagement.version,
        afficher_formateur: choix.afficher,
        consentement_image: choix.afficher && choix.image,
        consentement_image_version: etat.data!.ambassadeur.image.version,
      }),
    onSuccess: async () => { setOuverte(false); await requetes.invalidateQueries({ queryKey: ["publication", formationId] }); notifier("succes", "Demande de publication envoyée à l'organisme."); },
  });
  if (etat.isPending) return null;
  if (etat.error) return <Alerte ton="danger">{etat.error.message}</Alerte>;
  const e = etat.data;
  const d = e.demande;
  const libelle = !d || d.statut === "brouillon" ? "Demander la publication sur skills4mation.com" : d.statut === "acceptee" ? "Demander une mise à jour de la fiche" : d.statut === "refusee" ? "Renouveler la demande de publication" : null;
  const fermer = () => { setOuverte(false); void requetes.invalidateQueries({ queryKey: ["publication", formationId] }); };
  return (
    <Carte className="p-5">
      <h2 className="flex items-center gap-2 text-base font-semibold">
        <Globe className="size-4.5 text-accent" aria-hidden /> Publication sur le site
      </h2>
      <p className="mt-0.5 text-[13px] text-encre-2">Proposez cette formation au catalogue de skills4mation.com : l'organisme valide la fiche, puis la met en ligne — {e.delai}.</p>
      {d && d.statut !== "brouillon" && <StatutDemande d={d} delai={e.delai} />}
      {d && d.statut === "acceptee" && d.ambassadeur && !d.ambassadeur_retire_le && <p className="mt-1.5 text-[13px] text-encre-2">Programme ambassadeur : les demandes reçues via la page vous sont transmises{d.afficher_formateur && !d.image_retiree_le ? " ; votre nom et votre photo y figurent" : ""}.</p>}
      {d && d.statut === "acceptee" && (d.ambassadeur_retire_le || d.image_retiree_le) && <p className="mt-1.5 text-[13px] text-encre-2">Retrait enregistré ({[d.image_retiree_le && "affichage du nom et de la photo", d.ambassadeur_retire_le && "relais des demandes"].filter(Boolean).join(", ")}) : l'organisme republie la page. Gérez vos autorisations dans <Link to="/ma-vitrine" className="text-accent underline">Ma page sur le site</Link>.</p>}
      {d && d.statut === "acceptee" && <VuesDeLaFiche slug={d.slug} />}
      {libelle && (
        <Bouton className="mt-3" variante={d?.statut === "acceptee" ? "secondaire" : "primaire"} icone={<Send className="size-4" aria-hidden />} enCours={preparer.isPending} disabled={!e.parcours_enregistre} title={e.parcours_enregistre ? undefined : "Générez et enregistrez d'abord le parcours"} onClick={() => preparer.mutate()}>
          {libelle}
        </Bouton>
      )}
      {!e.parcours_enregistre && <p className="mt-2 text-xs text-encre-3">Disponible une fois le parcours généré et enregistré.</p>}

      <Modale ouverte={ouverte} fermer={fermer} titre="Demander la publication sur skills4mation.com" large>
        {demande && (
          <form className="space-y-5" onSubmit={(ev) => { ev.preventDefault(); soumettre.mutate(); }}>
            <PanneauVisuel demande={demande} images={e.images} alt={alt} setAlt={setAlt} maj={setDemande} />
            <PanneauAmbassadeur e={e.ambassadeur} choix={choix} changer={setChoix} />
            <ZoneTexte libelle="Message à l'organisme (facultatif)" rows={3} maxLength={2000} value={commentaire} onChange={(x) => setCommentaire(x.target.value)} aide="Public visé, mots-clés, précision sur le tarif…" />
            <Alerte titre="Et ensuite ?">L'organisme reçoit votre demande et l'accepte ou la refuse avec un motif. Après acceptation : {e.delai}. Vous suivez le statut ici.</Alerte>
            {soumettre.error && <Alerte ton="danger">{soumettre.error.message}</Alerte>}
            <div className="flex flex-wrap justify-end gap-2">
              <Bouton variante="discret" onClick={fermer}>Plus tard</Bouton>
              <Bouton type="submit" variante="signal" icone={<Send className="size-4" aria-hidden />} enCours={soumettre.isPending} disabled={choix.afficher && !choix.image} title={choix.afficher && !choix.image ? "Cochez l'autorisation d'utilisation de votre image, ou décochez l'affichage du nom et de la photo" : undefined}>Envoyer la demande</Bouton>
            </div>
          </form>
        )}
      </Modale>
    </Carte>
  );
}

// ——— Organisme ———

function Decision({ d, file, termine }: { d: DemandeATraiter; file: FilePublications; termine: () => void }) {
  const notifier = useNotifier();
  const [demande, setDemande] = useState<DemandePublication>(d);
  const [alt, setAlt] = useState(d.visuel_alt);
  const [categorie, setCategorie] = useState("");
  const [slug, setSlug] = useState(d.slug);
  const accepter = useMutation({
    mutationFn: () => api.post(`/admin/publications/${d.id}/decision`, { acceptee: true, categorie, slug, ...(demande.visuel_source !== "aucun" ? { visuel_alt: alt } : {}) }),
    onSuccess: () => { notifier("succes", "Publication acceptée : téléchargez la fiche pour la vitrine."); termine(); },
  });
  return (
    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); accepter.mutate(); }}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Selecteur libelle="Catégorie du catalogue" required value={categorie} onChange={(e) => setCategorie(e.target.value)}>
          <option value="">— Choisir —</option>
          {CATEGORIES_VITRINE.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
        </Selecteur>
        <Champ libelle="Adresse de la fiche (slug)" aide={`skills4mation.com/formations/${slug || "…"}`} value={slug} spellCheck={false} onChange={(e) => setSlug(e.target.value.trim())} />
      </div>
      <PanneauVisuel demande={demande} images={file.images} alt={alt} setAlt={setAlt} maj={setDemande} />
      <Alerte titre="Publication manuelle">À l'acceptation, le SaaS produit la fiche au format de la vitrine (JSON) et ses visuels WebP. La mise en ligne est un commit dans <code>vitrine/</code> : {file.delai}.</Alerte>
      {accepter.error && <Alerte ton="danger">{accepter.error.message}</Alerte>}
      <div className="flex justify-end">
        <Bouton type="submit" variante="primaire" icone={<Check className="size-4" aria-hidden />} enCours={accepter.isPending}>Accepter et produire la fiche</Bouton>
      </div>
    </form>
  );
}

function Refus({ d, termine }: { d: DemandeATraiter; termine: () => void }) {
  const notifier = useNotifier();
  const [motif, setMotif] = useState("");
  const refuser = useMutation({ mutationFn: () => api.post(`/admin/publications/${d.id}/decision`, { acceptee: false, motif }), onSuccess: () => { notifier("succes", "Demande refusée : le formateur est prévenu."); termine(); } });
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); refuser.mutate(); }}>
      <ZoneTexte libelle="Motif du refus" required rows={4} maxLength={2000} value={motif} onChange={(e) => setMotif(e.target.value)} aide="Transmis au formateur, qui pourra aménager sa formation et renouveler la demande." />
      {refuser.error && <Alerte ton="danger">{refuser.error.message}</Alerte>}
      <div className="flex justify-end">
        <Bouton type="submit" variante="danger" icone={<X className="size-4" aria-hidden />} enCours={refuser.isPending}>Refuser la publication</Bouton>
      </div>
    </form>
  );
}

const lienTelechargement = "inline-flex h-8 items-center gap-1.5 rounded-sm border border-trait-fort bg-carte px-2.5 text-[13px] font-medium hover:bg-papier-2";

/** File « Publications à traiter » de l'accueil de l'organisme (masquée quand il n'y a rien à traiter ni à télécharger). */
export function PublicationsATraiter() {
  const requetes = useQueryClient();
  const file = useQuery({ queryKey: ["publications-a-traiter"], queryFn: () => api.get<FilePublications>("/admin/publications") });
  const [action, setAction] = useState<{ d: DemandeATraiter; type: "accepter" | "refuser" } | null>(null);
  if (file.isPending) return <Chargement libelle="Chargement des publications…" />;
  if (file.error) return <div className="mb-6"><Alerte ton="danger" titre="Publications à traiter">{file.error.message}</Alerte></div>;
  const f = file.data;
  if (f.demandes.length === 0 && f.acceptees.length === 0) return null;
  const termine = () => { setAction(null); void requetes.invalidateQueries({ queryKey: ["publications-a-traiter"] }); };
  return (
    <section id="publications" aria-labelledby="titre-publications" className="mb-6 scroll-mt-24 rounded-lg border border-trait bg-carte p-4 shadow-carte sm:p-5">
      <h2 id="titre-publications" className="flex items-center gap-2 text-base font-semibold">
        <Globe className="size-4.5 text-accent" aria-hidden /> Publications à traiter
        <span className="chiffres rounded-full bg-attente px-2 text-[12px] font-semibold text-attente-encre">{f.demandes.length}</span>
      </h2>
      <p className="mt-0.5 text-[13px] text-encre-2">Formations que les formateurs proposent au catalogue de skills4mation.com — {f.delai}.</p>
      {f.demandes.length === 0 ? (
        <p className="mt-3 text-sm text-encre-3">Aucune demande en attente.</p>
      ) : (
        <ul className="mt-3 divide-y divide-trait">
          {f.demandes.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-3 py-3">
              {d.visuel_url ? <img src={d.visuel_url} alt="" width={96} height={64} className="h-16 w-24 shrink-0 rounded-sm object-cover" /> : <span className="grid h-16 w-24 shrink-0 place-items-center rounded-sm bg-papier-3 text-encre-3"><ImagePlus className="size-5" aria-hidden /></span>}
              <span className="min-w-0 flex-1 basis-48">
                <span className="block font-medium">{d.formation_titre}</span>
                <span className="block text-[13px] text-encre-2">{d.formateur} · demandée le {instantFr(d.demandee_le)} · {SOURCES[d.visuel_source].toLowerCase()}</span>
                {d.commentaire && <span className="mt-0.5 block text-[13px] text-encre-3">« {d.commentaire} »</span>}
              </span>
              <span className="flex gap-2">
                <Bouton taille="sm" variante="primaire" icone={<Check className="size-3.5" aria-hidden />} onClick={() => setAction({ d, type: "accepter" })}>Accepter…</Bouton>
                <Bouton taille="sm" variante="discret" icone={<X className="size-3.5" aria-hidden />} onClick={() => setAction({ d, type: "refuser" })}>Refuser…</Bouton>
              </span>
            </li>
          ))}
        </ul>
      )}
      {f.acceptees.length > 0 && (
        <div className="mt-3 border-t border-trait pt-3">
          <h3 className="text-[13px] font-semibold text-encre-2">Dernières acceptées — à ajouter à la vitrine</h3>
          <ul className="mt-2 space-y-2">
            {f.acceptees.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 basis-48">
                  {d.formation_titre} <span className="text-encre-3">· /formations/{d.slug} · acceptée le {instantFr(d.traitee_le)}</span>
                  {(d.image_retiree_le || d.ambassadeur_retire_le) && (
                    <span className="mt-0.5 block text-[13px] text-encre-2">
                      <Etiquette ton="attente">À republier</Etiquette>{" "}
                      {d.image_retiree_le ? `Autorisation d'affichage retirée le ${instantFr(d.image_retiree_le)} : sans le bloc « Votre formateur »` : ""}
                      {d.image_retiree_le && d.ambassadeur_retire_le ? " ; " : ""}
                      {d.ambassadeur_retire_le ? `relais des demandes retiré le ${instantFr(d.ambassadeur_retire_le)} : sans le formulaire ambassadeur` : ""}
                      . Retéléchargez la fiche.
                    </span>
                  )}
                </span>
                <a href={`/api/admin/publications/${d.id}/fiche`} download className={lienTelechargement}><FileJson className="size-3.5" aria-hidden /> Fiche JSON</a>
                <a href={`/api/admin/publications/${d.id}/paquet`} download className={lienTelechargement}><Download className="size-3.5" aria-hidden /> Paquet pour la vitrine</a>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Modale ouverte={action?.type === "accepter"} fermer={() => setAction(null)} titre={`Accepter la publication${action ? ` — ${action.d.formation_titre}` : ""}`} large>
        {action?.type === "accepter" && <Decision key={action.d.id} d={action.d} file={f} termine={termine} />}
      </Modale>
      <Modale ouverte={action?.type === "refuser"} fermer={() => setAction(null)} titre={`Refuser la publication${action ? ` — ${action.d.formation_titre}` : ""}`}>
        {action?.type === "refuser" && <Refus key={action.d.id} d={action.d} termine={termine} />}
      </Modale>
    </section>
  );
}
